import React, { useMemo, useState } from "react";
import {
  Box, Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Skeleton, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HistoryIcon from "@mui/icons-material/History";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ListAltIcon from "@mui/icons-material/ListAlt";
import dayjs from "dayjs";
import useFetchWithMsal from "../../../utils/useFetchWithMsal";
import { useGetClientServiceStatisticQuery } from "../../../services/ClientService";
import { BreakdownPanel, MetricPanel, StatTile, buildRotatingColorMap } from "../metricsPanels";

const fmtDate = (d) => (d ? d.format("M/D/YYYY") : "—");

// Per service type: split its occurrence dates into completed (before now) and upcoming, and
// keep the most recent / next date for the summary row.
const summarizeServices = (services) => {
  const now = dayjs();
  return (Array.isArray(services) ? services : []).map((s) => {
    const dates = (s?.endDates ?? [])
      .map((d) => dayjs(d?.date))
      .filter((d) => d.isValid())
      .sort((a, b) => b.valueOf() - a.valueOf());
    const past = dates.filter((d) => d.isBefore(now));
    const upcoming = dates.filter((d) => !d.isBefore(now));
    return {
      id: s?.id,
      name: s?.name || "Service",
      status: s?.status || "",
      count: Number(s?.count) || dates.length,
      dates,
      completed: past.length,
      upcoming: upcoming.length,
      last: past[0] ?? null,
      next: upcoming.length ? upcoming[upcoming.length - 1] : null,
    };
  });
};

// One expandable row in the Service History panel: summary line, then every occurrence date.
const ServiceHistoryRow = ({ row, color }) => {
  const [open, setOpen] = useState(false);
  const now = dayjs();
  const toggle = () => setOpen((v) => !v);
  return (
    <Box sx={{ borderBottom: "1px solid #F0F0F0", "&:last-child": { borderBottom: "none" } }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr auto", sm: "minmax(0,1.6fr) 0.9fr 0.9fr 0.9fr 28px" },
          alignItems: "center", columnGap: 1.5, rowGap: 0.5, py: 1, px: 0.5,
          cursor: "pointer", borderRadius: 1.5,
          "&:hover": { bgcolor: "rgba(29,158,117,0.04)" },
          "&:focus-visible": { outline: "2px solid #0072E5", outlineOffset: 1 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {row.name}
          </Typography>
          {row.status && (
            <Box
              component="span"
              sx={{
                flexShrink: 0, fontSize: 10.5, fontWeight: 700, lineHeight: "18px", px: 1, borderRadius: 999,
                bgcolor: "rgba(0,114,229,0.08)", color: "#0072E5", whiteSpace: "nowrap",
              }}
            >
              {row.status}
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", display: { xs: "none", sm: "block" }, fontVariantNumeric: "tabular-nums" }}>
          <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{row.completed}</Box> done
          {row.upcoming > 0 && (
            <>
              {" · "}
              <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{row.upcoming}</Box> upcoming
            </>
          )}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", display: { xs: "none", sm: "block" } }}>
          Last {fmtDate(row.last)}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", display: { xs: "none", sm: "block" } }}>
          Next {fmtDate(row.next)}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: 20, color: "text.secondary", justifySelf: "end",
            transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s ease",
          }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, pl: 3, pr: 0.5, pb: 1.25 }}>
          {row.dates.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>No dates recorded.</Typography>
          ) : row.dates.map((d, i) => {
            const future = !d.isBefore(now);
            return (
              <Box
                key={`${d.valueOf()}-${i}`}
                component="span"
                sx={{
                  fontSize: 11, lineHeight: "20px", px: 1, borderRadius: 999, fontVariantNumeric: "tabular-nums",
                  border: "1px solid", borderColor: future ? "rgba(0,114,229,0.35)" : "rgba(0,0,0,0.1)",
                  color: future ? "#0072E5" : "text.secondary", bgcolor: future ? "rgba(0,114,229,0.05)" : "#F7F9FC",
                }}
              >
                {fmtDate(d)}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * ServicesStatisticsPopup — a client's service history as a dialog of panels, in the same
 * visual language as the Clients page's Client Metrics dialog: overview tiles, service-type and
 * billable-status donuts, and an expandable per-service history list.
 * Opened from the "Statistics" link on an expanded client row and from the services
 * ("Progress Notes") section of the client page.
 *
 * Props: open, onClose, clientId, clientName
 */
export const ServicesStatisticsPopup = ({ open, onClose, clientId, clientName }) => {
  const { skip } = useFetchWithMsal();
  // Only fetch while open and for a real client (the old modal fetched on mount with an
  // undefined id). Refetch if the cached copy is older than 30s so stats reflect recent edits.
  const { data, isFetching } = useGetClientServiceStatisticQuery(clientId, {
    skip: skip || !open || !clientId,
    refetchOnMountOrArgChange: 30,
  });
  const isLoading = isFetching && !data;

  const rows = useMemo(() => summarizeServices(data?.services), [data]);
  const colorMap = useMemo(() => buildRotatingColorMap(rows.map((r) => r.name)), [rows]);

  const byStatus = useMemo(() => {
    const acc = {};
    rows.forEach((r) => {
      const k = r.status || "Unspecified";
      acc[k] = (acc[k] || 0) + r.count;
    });
    return Object.entries(acc).map(([label, value]) => ({ label, value }));
  }, [rows]);
  const statusColors = useMemo(() => buildRotatingColorMap(byStatus.map((s) => s.label)), [byStatus]);

  const total = rows.reduce((sum, r) => sum + r.count, 0);
  const completed = rows.reduce((sum, r) => sum + r.completed, 0);
  const upcoming = rows.reduce((sum, r) => sum + r.upcoming, 0);
  const lastRow = rows.filter((r) => r.last).sort((a, b) => b.last.valueOf() - a.last.valueOf())[0] ?? null;
  const nextRow = rows.filter((r) => r.next).sort((a, b) => a.next.valueOf() - b.next.valueOf())[0] ?? null;
  const byType = rows.map((r) => ({ label: r.name, value: r.count }));
  const sortedRows = rows.slice().sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  const isEmpty = !isLoading && rows.length === 0;

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Typography component="div" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>Service Statistics</Typography>
        {clientName && (
          <Typography component="div" sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>{clientName}</Typography>
        )}
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#F7F9FC", display: "flex", flexDirection: "column", gap: 2 }}>
        {isEmpty ? (
          <MetricPanel icon={ListAltIcon} title="Services">
            <Typography sx={{ fontSize: 13, color: "text.secondary", py: 3, textAlign: "center" }}>
              No services on record for this client yet.
            </Typography>
          </MetricPanel>
        ) : (
          <>
            <MetricPanel icon={EventNoteOutlinedIcon} title="Overview">
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
                <StatTile
                  label="Total Services"
                  value={total}
                  caption={`${rows.length} service type${rows.length === 1 ? "" : "s"}`}
                  icon={ListAltIcon}
                  isLoading={isLoading}
                />
                <StatTile
                  label="Completed"
                  value={completed}
                  caption={`${total > 0 ? Math.round((completed / total) * 100) : 0}% of total`}
                  icon={TaskAltIcon}
                  isLoading={isLoading}
                />
                <StatTile
                  label="Upcoming"
                  value={upcoming}
                  caption={nextRow ? `Next ${fmtDate(nextRow.next)}` : "None scheduled"}
                  icon={EventAvailableOutlinedIcon}
                  isLoading={isLoading}
                />
                <StatTile
                  label="Last Service"
                  value={lastRow ? fmtDate(lastRow.last) : "—"}
                  caption={lastRow ? lastRow.name : "None completed"}
                  icon={HistoryIcon}
                  isLoading={isLoading}
                />
              </Box>
            </MetricPanel>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <BreakdownPanel icon={CategoryOutlinedIcon} title="By Service Type" data={byType} colorMap={colorMap} isLoading={isLoading} />
              <BreakdownPanel icon={PaidOutlinedIcon} title="By Billable Status" data={byStatus} colorMap={statusColors} isLoading={isLoading} />
            </Box>

            <MetricPanel icon={HistoryIcon} title="Service History" subtitle={!isLoading ? "Select a service to see its dates" : undefined}>
              {isLoading ? (
                [0, 1, 2].map((i) => <Skeleton key={i} variant="text" sx={{ fontSize: 22 }} />)
              ) : (
                <Box>
                  {sortedRows.map((row) => (
                    <ServiceHistoryRow key={row.id ?? row.name} row={row} color={colorMap[row.name] || "#c7c7c7"} />
                  ))}
                </Box>
              )}
            </MetricPanel>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServicesStatisticsPopup;
